import { formatUserFacingError } from '@/lib/errors'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { companyService } from '../services/company.service'
import type { CompanyMemberDocument } from '@/types'
import type { QueryDocumentSnapshot } from '@/lib/firebase/firestore'

const PAGE_SIZE = 12

interface UseCompanyMembersResult {
  members: Array<CompanyMemberDocument & { id: string }>
  filtered: Array<CompanyMemberDocument & { id: string }>
  availableRoles: string[]
  positionGroupMap: Record<string, string>
  loading: boolean
  loadingMore: boolean
  hasMore: boolean
  loadMore: () => void
  error: string | null
}

export function useCompanyMembers(roleFilter: string = 'all'): UseCompanyMembersResult {
  const [members, setMembers] = useState<Array<CompanyMemberDocument & { id: string }>>([])
  const [availableRoles, setAvailableRoles] = useState<string[]>([])
  const [positionGroupMap, setPositionGroupMap] = useState<Record<string, string>>({})
  const [cursor, setCursor] = useState<QueryDocumentSnapshot<CompanyMemberDocument> | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    Promise.all([
      companyService.getPublishedMembersPage(PAGE_SIZE, null),
      companyService.getActivePositions(),
    ])
      .then(([page, positions]) => {
        setMembers(page.items)
        setCursor(page.lastDoc)
        setHasMore(page.hasMore)
        setAvailableRoles(positions.map((p) => p.title))
        const groupMap: Record<string, string> = {}
        for (const p of positions) {
          if (p.group) groupMap[p.title] = p.group
        }
        setPositionGroupMap(groupMap)
      })
      .catch((err: unknown) => setError(formatUserFacingError(err, 'Failed to load company')))
      .finally(() => setLoading(false))
  }, [])

  const loadMore = useCallback(() => {
    if (!hasMore || loadingMore) return
    setLoadingMore(true)
    companyService
      .getPublishedMembersPage(PAGE_SIZE, cursor)
      .then((page) => {
        setMembers((prev) => [...prev, ...page.items])
        setCursor(page.lastDoc)
        setHasMore(page.hasMore)
      })
      .catch((err: unknown) => setError(formatUserFacingError(err, 'Failed to load more')))
      .finally(() => setLoadingMore(false))
  }, [cursor, hasMore, loadingMore])

  const filtered = useMemo(
    () => (roleFilter === 'all' ? members : members.filter((m) => m.roles.includes(roleFilter))),
    [members, roleFilter]
  )

  return { members, filtered, availableRoles, positionGroupMap, loading, loadingMore, hasMore, loadMore, error }
}
